import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useWeekNoteEditor,
  useWeekNotesData,
} from "../../../../../hooks/annual-planner";
import Toast from "../../../../../parts/toast";
import {
  milestoneLabel,
  readablePhaseColorOnDark,
  readablePhaseColorOnLight,
} from "../../../../../utils/annual-planner-helpers";
import { PhasePill } from "./Pills";

export const WeekNotesPanel = ({
  selectedWeek,
  datesAttachments,
  phases,
  nutritionDatesAttachments,
  nutritionPhases,
  clientGoals,
  events,
  phasesDescriptions,
  setPhasesDescriptions,
  athleteId,
  organizationCoachId,
  focusSignal,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const panelRef = useRef(null);
  const textareaRef = useRef(null);
  const { trainingPhase, nutritionPhase, milestones, weekComment } =
    useWeekNotesData({
      selectedWeek,
      datesAttachments,
      phases,
      nutritionDatesAttachments,
      nutritionPhases,
      clientGoals,
      events,
      phasesDescriptions,
    });

  // Client-side stand-in for the server-driven flash() Toast (page.js reads
  // locals.flash() at SSR render time, so it only works after a full page
  // reload) - save/delete here are fetch-without-reload, same situation
  // Nutrition Progress already solved (SummaryHero.js sets this precedent).
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);
  const showToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type: "success", message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  };

  const { noteText, setNoteText, errorMessage, canSave, handleSave } =
    useWeekNoteEditor({
      selectedWeek,
      weekComment,
      athleteId,
      organizationCoachId,
      setPhasesDescriptions,
      onSaved: () => showToast(t("annualPlanner.noteSaved")),
      onDeleted: () => showToast(t("annualPlanner.noteDeleted")),
    });

  // focusSignal only bumps from a click elsewhere (Calendar bubble, List
  // view's "Add notes") - never on mount, never from the stepper - so this
  // is exactly "the panel just got aimed at a new week
  // from off-screen, bring it and the textarea into view".
  useEffect(() => {
    if (focusSignal === 0) return;
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    textareaRef.current?.focus();
  }, [focusSignal]);

  return (
    <div className="week-notes-panel" ref={panelRef}>
      <div className="week-notes-panel-header">
        <div>
          <h3>{t("annualPlanner.notesTitle")}</h3>
          <span className="week-notes-panel-subtitle">
            {t("annualPlanner.week")}{" "}
            {moment(selectedWeek).format("MMM D, YYYY")}
          </span>
        </div>
        <span className="week-notes-panel-hint">
          {t("annualPlanner.oneNoteBothPhases")}
        </span>
      </div>

      {(trainingPhase || nutritionPhase || milestones.length > 0) && (
        <div className="week-notes-panel-chips">
          {trainingPhase && (
            <PhasePill name={trainingPhase.name} color={trainingPhase.color} />
          )}
          {nutritionPhase && (
            <PhasePill
              name={nutritionPhase.name}
              color={nutritionPhase.color}
            />
          )}
          {milestones.map((milestone, milestoneIndex) => (
            <span
              key={`${milestone.kind}-${milestoneIndex}`}
              className={`summary-pill week-notes-milestone${milestone.color ? " colored" : ""}`}
              style={
                milestone.color
                  ? {
                      "--phase-pill-color": milestone.color,
                      "--phase-pill-color-readable": readablePhaseColorOnDark(
                        milestone.color
                      ),
                      "--phase-pill-color-readable-light":
                        readablePhaseColorOnLight(milestone.color),
                    }
                  : undefined
              }
            >
              {milestoneLabel(milestone, t)}
              <span className="week-notes-milestone-countdown">
                {t("annualPlanner.milestoneCountdown", {
                  count: milestone.countdownWeeks,
                })}
              </span>
            </span>
          ))}
        </div>
      )}

      <textarea
        ref={textareaRef}
        className="week-notes-textarea"
        placeholder={t("annualPlanner.notesPlaceholder")}
        value={noteText}
        onChange={({ target: { value } }) => setNoteText(value)}
      />

      {errorMessage && (
        <span className="week-notes-panel-error">{errorMessage}</span>
      )}

      <div className="week-notes-panel-footer">
        {/* Notes are always coach-only (confirmed with product) - no
            toggle, just a fixed label. */}
        <span className="week-notes-visibility-note">
          {t("annualPlanner.onlyVisibleToYou")}
        </span>

        <div className="week-notes-panel-actions">
          <button
            type="button"
            data-button-type="primary"
            disabled={!canSave}
            onClick={handleSave}
          >
            {t("common:save")}
          </button>
        </div>
      </div>

      <Toast
        open={Boolean(toast)}
        type={toast?.type ?? "success"}
        message={toast?.message ?? ""}
      />
    </div>
  );
};
